import { z } from 'zod';
/**
 * Connecting a monday.com account, and importing a board from it.
 *
 * Import only. `CLAUDE.md` §3 confines monday to one adapter that reads — there
 * is no shape here for writing anything back, because nothing does.
 *
 * **No token ever comes back.** It goes one way, on the way in. Everything the
 * server returns about a connection is the account it belongs to, which is what
 * a screen needs to say whose it is.
 */
/** monday personal API tokens are long JWTs. Bounded so a paste of a whole file is refused. */
export const connectMondayRequestSchema = z.object({
    token: z.string().trim().min(20, 'that does not look like a monday token').max(4_000),
});
const mondayAccountSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
});
/**
 * Three answers rather than two.
 *
 * A token that monday refuses is an ordinary mistake — a typo, a revoked key —
 * and belongs beside the box rather than as a failure. `connected` carries the
 * account so the screen can say who it is without a second request.
 */
export const connectMondayResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('connected'), account: mondayAccountSchema }),
    z.object({ status: z.literal('refused') }),
]);
/** Null rather than absent: "nothing is connected" is the ordinary state, and said plainly. */
export const mondayConnectionSchema = z.object({
    connection: z
        .object({
        account: mondayAccountSchema,
        connectedAt: z.string().datetime(),
    })
        .nullable(),
});
export const mondayBoardListSchema = z.object({
    boards: z.array(z.object({ boardId: z.string(), name: z.string() })),
});
export const mondayBoardSchema = z.object({
    boardId: z.string(),
    name: z.string(),
    items: z.number().int(),
    columns: z.array(z.object({ columnId: z.string(), title: z.string(), type: z.string() })),
    groups: z.array(z.object({ groupId: z.string(), title: z.string() })),
});
/**
 * What each monday column becomes.
 *
 * `skip` is a real answer and the default. A board has columns nobody wants
 * imported, and leaving one out has to be something somebody chooses rather
 * than something they forget.
 */
const mondayTargetSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('skip') }),
    z.object({ kind: z.literal('email') }),
    z.object({ kind: z.literal('name') }),
    z.object({ kind: z.literal('field'), key: z.string().min(1).max(64) }),
    /** Whether they are subscribed, read from the column's own text. */
    z.object({ kind: z.literal('subscription') }),
]);
export const importFromMondayRequestSchema = z.object({
    boardId: z.string().min(1),
    /** Keyed by monday column id. Columns left out are skipped. */
    mapping: z.record(z.string(), mondayTargetSchema),
    /**
     * Whether each monday group becomes a segment of the same name.
     *
     * The contacts in a group are connected to its segment in the same
     * transaction as the rows, so a group that imports is a segment that is
     * populated — never one without the other.
     */
    groupsAsSegments: z.boolean(),
    /** Segments every imported contact joins, whatever group they were in. */
    segmentIds: z.array(z.string().uuid()).max(20),
});
/**
 * What the import did.
 *
 * `withoutEmail` is its own number rather than a rejection, because it is the
 * one that says the wrong column was mapped: a board where every item lacks an
 * address is a mapping mistake, not forty thousand bad rows.
 */
/**
 * What a board's webhook is allowed to do.
 *
 * Two switches rather than one, because they are not the same risk. Creating a
 * contact is additive and reversible; deleting one removes them and their
 * segment memberships, and a board being tidied looks exactly like a board
 * being corrected. Both start off.
 */
export const mondaySyncSchema = z.object({
    onItemCreated: z.boolean(),
    onItemDeleted: z.boolean(),
});
/**
 * What queueing an import answers: where to watch it, and nothing else.
 *
 * The counts used to come back from this request because the request did the
 * work. It does not any more — it writes a row and returns, and everything the
 * screen shows afterwards comes from asking about the job.
 */
export const queuedImportSchema = z.object({ jobId: z.string().uuid() });
/**
 * How far an import has got.
 *
 * `itemsTotal` is null until the board has been opened, which is a second or
 * two after queueing — a bar with no denominator yet is honest, a denominator
 * invented from nothing is not.
 */
export const importJobSchema = z.object({
    jobId: z.string(),
    boardId: z.string(),
    boardName: z.string().nullable(),
    status: z.enum(['queued', 'running', 'done', 'failed']),
    itemsTotal: z.number().int().nullable(),
    itemsRead: z.number().int(),
    added: z.number().int(),
    duplicates: z.number().int(),
    rejectedTotal: z.number().int(),
    withoutEmail: z.number().int(),
    unsubscribed: z.number().int(),
    segments: z.array(z.string()),
    /** Why it stopped, in words to show. Null unless it failed. */
    failureReason: z.string().nullable(),
    finishedAt: z.string().datetime().nullable(),
});
/**
 * A freshly minted webhook URL.
 *
 * Its own response because it is the one moment it exists in a readable form:
 * only the token's fingerprint is stored, so nothing can answer this twice for
 * the same token.
 */
export const mondayWebhookSchema = z.object({ webhookUrl: z.string().min(1) });
//# sourceMappingURL=monday.js.map